import { ComponentType, ReactNode } from 'react';

import { DefaultMantineColor, Stack, Text, ThemeIcon } from '@mantine/core';
import dayjs from 'dayjs';

import { routes } from '@/routes';
import FileIcon from '@/assets/icons/FileIcon';
import InformationIcon from '@/assets/icons/InformationIcon';
import SignatureFilledIcon from '@/assets/icons/SignatureFilledIcon';
import StatusWarningIcon from '@/assets/icons/StatusWarningIcon';

import { DeviceType } from '@/types/matchDevice';
import { TemplateFieldMappings, TemplateObject } from '@/types/reportTemplate';
import { ReportColor, ReportType, ReportTypeTitleMap } from '@/types/transmissionReport';

import formatDate from '../formatDate';

export const CLINICAL_NOTES_LIMIT = 20;

export const TRANSMISSION_REPORTS_LIMIT = 50;
export const MAX_ALERT_CARDS = 3;
export const NOTE_CHAR_LIMIT = 2000;
export const MAX_IMAGES_PER_ALERT = 3;
export const LONG_HYPHEN = '—'; // em dash. Used for empty fields in the UI. different from the regular hyphen

export const AUTOSAVE_DELAY = 1000 * 10; // 10 seconds

export const UNUSED_DATA_RETENTION_SECONDS = 86400;

export enum TimeOfDay {
  MORNING = 'morning',
  AFTERNOON = 'afternoon',
  EVENING = 'evening'
}

export const patientStatusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
  { label: 'Paused', value: 'Paused' }
];

export function capitalize(str: string): string {
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export const DATE_FORMAT = 'MM/DD/YYYY';

export const EMPTY_PLACEHOLDER = '-';

// Main Side Nav
export const SIDE_NAV_OPEN_WIDTH = 250;
export const SIDE_NAV_COLLAPSED_WIDTH = 80;
export const TOP_NAV_HEIGHT = 52;

// Reports page Sidebars
export const ACCORDION_SIDE_NAV_WIDTH = 300;
export const CLINICAL_NOTES_SIDE_NAV_WIDTH = 500;

/**
 * 24-11:59 morning
 * 12-17:59 afternoon
 * 18-23:59 evening
 * @param hour Accepts numbers from 0 to 23. If the range is exceeded, it will bubble up to the day.
 * @returns TimeOfDay enum | undefined
 */
export const getTimeOfday = (hour?: number): TimeOfDay => {
  const currentHour = hour ?? dayjs().get('hour');

  if (currentHour < 12) return TimeOfDay.MORNING;
  else if (currentHour >= 12 && currentHour < 18) return TimeOfDay.AFTERNOON;
  else return TimeOfDay.EVENING;
};

export interface TransmissionReportColorArrayItem {
  value: ReportColor;
  label: string;
  button?: string;
  signAllButton?: boolean;
  Icon: ComponentType<{ size?: number; p?: number }>;
}

export const TransmissionReportColorArray: TransmissionReportColorArrayItem[] = [
  {
    value: ReportColor.Red,
    label: 'Urgent',
    Icon: ({ size, p }: { size?: number; p?: number }) => (
      <ThemeIcon color="red" variant="light" size={size ?? 60} p={p ?? 16} radius={'md'}>
        <InformationIcon />
      </ThemeIcon>
    )
  },
  {
    value: ReportColor.Yellow,
    label: 'Require Review',
    Icon: ({ size, p }: { size?: number; p?: number }) => (
      <ThemeIcon color="yellow" variant="light" size={size ?? 60} p={p ?? 16} radius={'md'}>
        <StatusWarningIcon />
      </ThemeIcon>
    )
  },
  {
    value: ReportColor.Green,
    label: 'Require Signature',
    signAllButton: true,
    Icon: ({ size, p }: { size?: number; p?: number }) => (
      <ThemeIcon color="green" variant="light" size={size ?? 60} p={p ?? 16} radius={'md'}>
        <SignatureFilledIcon />
      </ThemeIcon>
    )
  },
  {
    value: ReportColor.Gray,
    label: 'Require Triage',
    Icon: ({ size, p }: { size?: number; p?: number }) => (
      <ThemeIcon color="gray" variant="light" size={size ?? 60} p={p ?? 16} radius={'md'}>
        <FileIcon />
      </ThemeIcon>
    )
  }
];

export const IN_CLINIC_REPORT_TYPES = [ReportType.InClinic, ReportType.RemoteInClinic];

export const isReportInClinic = (transmissionType: string | undefined) =>
  transmissionType ? IN_CLINIC_REPORT_TYPES.includes(transmissionType as ReportType) : false;

export const fetchNextColor = (currentColor: ReportColor) => {
  switch (currentColor) {
    case ReportColor.Red:
      return ReportColor.Yellow;
    case ReportColor.Yellow:
      return ReportColor.Green;
    case ReportColor.Green:
      return ReportColor.Gray;
    case ReportColor.Gray:
      return null;
    default:
      return null;
  }
};

const colors: DefaultMantineColor[] = [
  'dark',
  '#AD1374',
  'grape',
  'violet',
  'indigo',
  'blue',
  'cyan',
  'lime',
  'orange',
  'teal'
];

export function getAvatarColor(word: string): DefaultMantineColor {
  const number = generateNumberFromName(word);
  const index = number % colors.length;
  return colors[index];
}

function generateNumberFromName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash = hash & hash; // Convert to a 32bit integer
  }
  return Math.abs(hash);
}

const colorNewReportMapping: Record<ReportColor, string> = {
  [ReportColor.Red]: 'Red Alert',
  [ReportColor.Yellow]: 'Yellow Alert',
  [ReportColor.Green]: 'Routine',
  [ReportColor.Gray]: 'Untriaged'
} as any;

export function getNewReportTitle(
  deviceType: string,
  transmissionType: ReportType,
  color: ReportColor,
  flagHeartFailure: boolean,
  isInClinicReport?: boolean
): string {
  let prefix = colorNewReportMapping[color] ?? 'Default';

  if (isInClinicReport) {
    return `${prefix} In-Clinic`;
  }

  if (flagHeartFailure && prefix !== 'Default') {
    if (prefix === 'Routine') {
      prefix = 'Heart Failure';
    } else {
      prefix += ' Heart Failure';
    }
  }
  if (prefix === 'Routine') prefix = 'Routine Report';

  return `${prefix}`;
}

export function getReportName(
  deviceType: string,
  transmissionType: ReportType,
  color: ReportColor,
  flagHeartFailure: boolean
): string {
  const parts: string[] = [];

  // Determine device type
  if (deviceType && deviceType !== 'no_device_type') {
    parts.push(deviceType === DeviceType.PM ? 'Pacemaker' : deviceType);
  }

  // Determine alert color
  if (color === ReportColor.Red) {
    parts.push('Red Alert');
  } else if (color === ReportColor.Yellow) {
    parts.push('Yellow Alert');
  }

  const isInClinic = [ReportType.InClinic, ReportType.RemoteInClinic].includes(transmissionType);
  const isAlert = [ReportColor.Red, ReportColor.Yellow].includes(color);

  // Add In-Clinic if applicable
  if (isInClinic) {
    parts.push('In-Clinic');
  }

  // Add Routine if not an in-clinic and if it's not an alert
  if (!isInClinic && !isAlert && !flagHeartFailure) {
    parts.push('Routine');
  }

  // Add Heart Failure if flagged
  if (flagHeartFailure) {
    parts.push('Heart Failure');
  }

  parts.push('Report');
  return parts.join(' ');
}

export function getReviewReportName(
  deviceType: string,
  transmissionType: ReportType,
  flagHeartFailure: boolean
): string {
  let title = '';

  if (deviceType && deviceType !== 'no_device_type') {
    title = deviceType === DeviceType.PM ? 'Pacemaker' : deviceType;
  }

  if (
    transmissionType &&
    [ReportType.InClinic, ReportType.Manual, ReportType.RemoteInClinic].includes(transmissionType)
  ) {
    title = ReportTypeTitleMap[transmissionType];
  }

  return title + (flagHeartFailure ? ' Heart Failure' : '') + ' Report';
}

export enum FileUploadStoragePath {
  TransmissionReportImage = 'transmission-reports-alerts-images',
  UserProfileImage = 'user-profile-image',
  OrganizationDocs = 'organization-docs',
  PatientDocs = 'patient-docs',
  Default = 'default'
}

export const DATE_FORMAT_REGEX =
  /(?:(?:(?:0[1-9]|1[0-2])\/(?:0[1-9]|1\d|2[0-8])|(?:0[13-9]|1[0-2])\/(?:29|30)|(?:0[13578]|1[02])\/31)\/[1-9]\d{3}|02\/29(?:\/[1-9]\d(?:0[48]|[2468][048]|[13579][26])|(?:[2468][048]|[13579][26])00))/;

export const getZeroHourDbDate = (date?: string | null): Date | null => {
  if (!date) return null;
  const dateOnly = new Date(date);
  if (isNaN(dateOnly.getTime())) return null; // Handle invalid date
  // Set the time to midnight (00:00:00)
  dateOnly.setHours(0, 0, 0, 0);
  return dateOnly;
};

export const checkReportEditMode = () => {
  const currentPath = window.location.pathname;
  const editReportByIdRegexPath = /^\/physician\/transmissions\/\d+\/edit$/;

  const isReportEditMode =
    currentPath.includes(routes.physician.editReport) || editReportByIdRegexPath.test(currentPath);
  return isReportEditMode;
};

export const getDateTimeWithTimezone = () => {
  const localDate = new Date();
  const formattedDate = dayjs(localDate).format('MM-DD-YYYY hh:mm A');

  const timezone = localDate
    .toLocaleDateString(undefined, { timeZoneName: 'short' })
    .split(' ')
    .pop();

  return `${formattedDate} ${timezone}`;
};

export const getEncodedDateTimeWithTimezone = () => {
  const localDate = new Date();
  const formattedDate = encodeURIComponent(dayjs(localDate).format('MM-DD-YYYY hh:mm A'));

  const timezone = localDate
    .toLocaleDateString(undefined, { timeZoneName: 'short' })
    .split(' ')
    .pop();

  return `${formattedDate}%20${timezone}`;
};

export const getCurrentDate = (format: string) => {
  const localDate = new Date();
  const dd = dayjs(localDate).format(format);

  return String(dd);
};

export const getTomorrow = (): Date => {
  return dayjs().add(1, 'day').startOf('day').toDate();
};

const motivationLines = [
  'Your dedication is inspiring',
  'You are making an impact',
  'Keep up the amazing work',
  'You are creating positive change',
  'Keep up the great work',
  'You are making a difference',
  'Great job'
];

export const randomMotivationLine = () =>
  `${motivationLines[Math.floor(Math.random() * motivationLines.length)]}!`;

export enum NotesTabType {
  All = 'all',
  Standard = 'standard',
  Pinned = 'pinned',
  Archived = 'archived'
}

export const YES_NO_OPTIONS = [
  {
    label: 'Yes',
    value: 'true'
  },
  {
    label: 'No',
    value: 'false'
  }
];

export const fetchDayClassname = (date: Date, value: any) => {
  const today = new Date();
  const isDateToday = formatDate(date.toISOString()) === formatDate(today);
  return !value && isDateToday ? 'currentDay' : '';
};

export const dateFormatRule = {
  pattern: {
    value: DATE_FORMAT_REGEX,
    message: 'Date is invalid or has wrong format (month/day/year)'
  },
  required: 'Field is required'
};

export const fetchToastHtml = (templateObjects: TemplateObject[]): ReactNode => {
  const elementArr: JSX.Element[] = [];
  const templateFields: Set<string> = new Set();
  templateObjects.forEach((templateObject) => {
    for (const val in templateObject) {
      templateFields.add(val);
    }
  });
  for (const val of Array.from(templateFields)) {
    const key = TemplateFieldMappings[val];
    elementArr.push(<Text>• {key}</Text>);
  }
  return (
    <Stack gap={0}>
      <Text>Following Fields have been updated</Text>
      {elementArr}
    </Stack>
  );
};

export { usStateOptions } from '@/utils/usStates';

export const NULL_OPTION = { value: 'null', label: '-' };

export const sexOptions = [
  { label: 'Female', value: 'female' },
  { label: 'Male', value: 'male' },
  { label: 'Other', value: 'other' },
  { label: 'Unknown', value: 'unknown' },
  { label: 'Ambiguous', value: 'ambiguous' },
  { label: 'Not applicable', value: 'not_applicable' },
  { label: 'Non-Binary', value: 'non_binary' }
];
